import { useCallback, useEffect, useMemo, useRef, useState, UIEvent, useLayoutEffect } from 'react'

import { Row, Table as ReactTableType } from '@tanstack/react-table'
import { debounce } from 'lodash-es'
import { useShallow } from 'zustand/react/shallow'

import {
  BodyRow,
  fetchMoreOnBottomReached,
  getColumnWidth,
  HeadRow,
  InfinityProps,
  InstanceTransferProps,
  LoadingBodyRow,
  ActionMenu as ActionMenuWrapper,
  fetchMoreOnTopReached,
  Wrapper,
  Root,
  Head,
  Body,
  ScrollToTopButton,
} from '@shared/components/Table'
import {
  useScrollRestorationStore,
  selectScrollRestorationSetters,
  useSelectLastKnownScrollPosition,
} from 'stores/scrollRestoration'

type InfiniteInstanceProps<D> = {
  tableInstance: ReactTableType<D>
  enableSelection: boolean
} & InstanceTransferProps<D> &
  InfinityProps['infinityProps'] &
  InfinityProps['previousInfinityProps']

const InfiniteInstanceRaw = <D,>({
  tableInstance,
  enableSelection,
  enableLinking,
  LinkProps,
  excludeFieldsFromLinking,
  enableClickAction,
  clickAction,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  onNextTrigger,
  hasPreviousPage,
  isFetchingPreviousPage,
  fetchPreviousPage,
  onPreviousTrigger,
  isInitialLoadingGlobal,
  isInitialLoadingByRowByID,
  RowActions,
  ActionMenuProps,
  renderSubComponent,
  hideHeader,
  onScroll,
  ScrollTopButton,
}: InfiniteInstanceProps<D>) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()

  const selectedRow = useRef<Row<D>>(undefined)
  const parentRef = useRef<HTMLDivElement>(null)

  const { setLastKnownScrollPosition } = useScrollRestorationStore(useShallow(selectScrollRestorationSetters))
  const lastKnownScrollPosition = useRef(useSelectLastKnownScrollPosition(window.location.pathname))

  const { rows } = tableInstance.getRowModel()

  const fetchMore = useCallback(async () => {
    if (fetchPreviousPage) {
      await fetchMoreOnTopReached({
        containerRefElement: parentRef.current,
        hasPreviousPage,
        isFetchingPreviousPage,
        fetchPreviousPage,
        onTrigger: onPreviousTrigger,
      })
    }
    fetchMoreOnBottomReached({
      containerRefElement: parentRef.current,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
      onTrigger: onNextTrigger,
    })
  }, [
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    onNextTrigger,
    onPreviousTrigger,
  ])

  const debouncedHandleScroll = useMemo(
    () =>
      debounce((value: number) => {
        setLastKnownScrollPosition(window.location.pathname, value)
      }, 500),
    [setLastKnownScrollPosition],
  )

  const handleScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      fetchMore()
      onScroll?.(parentRef.current?.scrollTop || 0)

      return debouncedHandleScroll(e.currentTarget.scrollTop)
    },
    [debouncedHandleScroll, fetchMore, onScroll],
  )

  const handleActionMenuClose = useCallback(() => {
    setAnchorEl(undefined)

    setTimeout(() => {
      selectedRow.current = undefined
    }, 300)
  }, [])

  const columnWidth = useMemo(
    () => getColumnWidth({ tableInstance, enableSelection, enableActionColumn: !!RowActions }),
    [RowActions, enableSelection, tableInstance],
  )

  useLayoutEffect(() => {
    if (lastKnownScrollPosition.current) {
      parentRef.current?.scrollTo({ top: lastKnownScrollPosition.current })
    }
  }, [])

  useEffect(() => {
    fetchMore()
  }, [fetchMore])

  return (
    <Wrapper ref={parentRef} onScroll={handleScroll}>
      {parentRef.current?.scrollTop && parentRef.current.scrollTop > 0 ? (
        ScrollTopButton ? (
          ScrollTopButton(parentRef)
        ) : (
          <ScrollToTopButton
            onClick={() => {
              parentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
            }}
          />
        )
      ) : null}
      <Root>
        {!hideHeader && (
          <Head>
            {tableInstance.getHeaderGroups().map((headerGroup) => (
              <HeadRow
                key={headerGroup.id}
                tableInstance={tableInstance}
                row={headerGroup}
                columnWidth={columnWidth}
                enableSelection={enableSelection}
                enableActionColumn={!!RowActions}
                isInitialLoading={isInitialLoadingGlobal}
              />
            ))}
          </Head>
        )}
        {isInitialLoadingGlobal ? (
          <Body>
            {[...Array(10)].map((_, idx) => (
              <LoadingBodyRow
                key={idx}
                tableInstance={tableInstance}
                columnWidth={columnWidth}
                enableSelection={enableSelection}
                enableActionColumn={!!RowActions}
              />
            ))}
          </Body>
        ) : (
          <Body>
            {rows.map((row) => (
              <BodyRow
                key={row.id}
                tableInstance={tableInstance}
                row={row}
                columnWidth={columnWidth}
                isInitialLoading={isInitialLoadingGlobal}
                isInitialLoadingByRowByID={isInitialLoadingByRowByID}
                enableSelection={enableSelection}
                enableLinking={enableLinking}
                LinkProps={LinkProps}
                excludeFieldsFromLinking={excludeFieldsFromLinking}
                enableClickAction={enableClickAction}
                clickAction={clickAction}
                AnimatedButtonProps={
                  RowActions
                    ? {
                        open: !!anchorEl && selectedRow.current?.index === row.index,
                        IconButtonProps: {
                          onClick: (e) => {
                            selectedRow.current = row
                            const target = e.currentTarget

                            setTimeout(() => {
                              setAnchorEl(target)
                            }, 0)
                          },
                        },
                        IconProps: {
                          fontSize: 'medium',
                        },
                      }
                    : undefined
                }
                renderSubComponent={renderSubComponent}
              />
            ))}
            {isFetchingNextPage && (
              <BodyRow
                tableInstance={tableInstance}
                row={[...rows].reverse()[0]}
                columnWidth={columnWidth}
                isInitialLoading
              />
            )}
          </Body>
        )}
      </Root>

      {!!RowActions && (
        <ActionMenuWrapper
          {...(selectedRow.current && ActionMenuProps(selectedRow.current))}
          anchorEl={anchorEl}
          onClose={handleActionMenuClose}
        >
          {!!selectedRow.current && RowActions({ row: selectedRow.current, onClose: handleActionMenuClose })}
        </ActionMenuWrapper>
      )}
    </Wrapper>
  )
}

export const InfiniteInstance = InfiniteInstanceRaw
